import { JSX, useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { useOpstelClient } from "@opstel/contexts/opstelClient/useOpstelClient";
import { useCurrentInstanceMetadata } from "@shared/common/contexts/instanceMetadata/useCurrentInstanceMetadata";
import { ErrorState } from "@shared/common/components/State/ErrorState";
import { SelectScenarioBorders } from "./ScenarioBorderSelection";
import {
  scenarioBorderSelectionSchema,
  ScenarioBorderSelection,
} from "@opstel/query/v2/models/scenarioBorderSelection";
import {
  scenarioBorderTrainSchema,
  ScenarioBorderTrain,
} from "@opstel/query/v2/models/scenarioBorderTrain";
import { instances } from "@opstel/query/queryKeys/instanceKeys";
import z from "zod";

interface ScenarioBorderSelectionWrapperProps {
  readonly onClose: () => void;
}

export function ScenarioBorderSelectionWrapper({
  onClose,
}: Readonly<ScenarioBorderSelectionWrapperProps>): JSX.Element {
  const { knopClient } = useKnopClient();
  const { opstelClient } = useOpstelClient();
  const { instanceId } = useCurrentInstanceId();
  const queryClient = useQueryClient();

  const {
    data: appearingScenarioBorderTrains,
    error: appearingScenarioBorderTrainsError,
    isLoading: appearingScenarioBorderTrainsIsLoading,
  } = useQuery({
    queryKey: instances.current(instanceId).appearingScenarioBorderTrains,
    queryFn: () => opstelClient.getAllAppearingScenarioBorderTrains({ instanceId: instanceId! }),
    select: (borderTrains): ScenarioBorderTrain[] =>
      z.array(scenarioBorderTrainSchema).parse(borderTrains),
  });

  const {
    data: disappearingScenarioBorderTrains,
    error: disappearingScenarioBorderTrainsError,
    isLoading: disappearingScenarioBorderTrainsIsLoading,
  } = useQuery({
    queryKey: instances.current(instanceId).disappearingScenarioBorderTrains,
    queryFn: () => opstelClient.getAllDisappearingScenarioBorderTrains({ instanceId: instanceId! }),
    select: (borderTrains): ScenarioBorderTrain[] =>
      z.array(scenarioBorderTrainSchema).parse(borderTrains),
  });

  const {
    data: scenarioBorderSelectionData,
    error: scenarioBorderSelectionError,
    isLoading: scenarioBorderSelectionIsLoading,
  } = useQuery({
    queryKey: instances.current(instanceId).scenarioBorderSelections,
    queryFn: () => opstelClient.getAllScenarioBorderSelections({ instanceId: instanceId! }),
    select: (rawData): ScenarioBorderSelection[] =>
      z.array(scenarioBorderSelectionSchema).parse(rawData),
  });

  const {
    data: sbdData,
    error: sbdDataError,
    isLoading: sbdDataIsLoading,
  } = useQuery({
    queryKey: instances.current(instanceId).sbdData,
    queryFn: () => knopClient.getSbdData(instanceId),
  });

  const {
    data: instanceMetadata,
    error: instanceMetadataError,
    isLoading: instanceMetadataIsLoading,
  } = useCurrentInstanceMetadata();

  const [scenarioBorderSelectionUpdates, setScenarioBorderSelectionUpdates] = useState<
    Map<string, ScenarioBorderSelection>
  >(new Map());

  const updateScenarioBorderSelection = useCallback((params: ScenarioBorderSelection) => {
    setScenarioBorderSelectionUpdates((prev) => new Map(prev).set(params.scenarioTrainId, params));
  }, []);

  const mutateScenarioBorderSelections = useMutation({
    mutationFn: (mutatedSelections: ScenarioBorderSelection[]) => {
      if (!mutatedSelections || mutatedSelections.length === 0) return Promise.resolve();
      return opstelClient.updateScenarioBorderSelections(
        { instanceId: instanceId! },
        mutatedSelections.map((mutatedSelection) => ({
          scenarioTrainId: mutatedSelection.scenarioTrainId,
          scenarioBorderTrack: mutatedSelection.scenarioBorderTrack,
        })),
      );
    },
    onError: (err) => {
      console.error(err);
      messageToast({
        type: "error",
        message: "Daggrenzen opslaan is mislukt. Neem contact op met de applicatiebeheerder.",
      });
    },
    onSuccess: () => {
      setScenarioBorderSelectionUpdates(new Map());
      return queryClient.invalidateQueries({
        queryKey: instances.current(instanceId).scenarioBorderSelections,
      });
    },
  });

  // Combine filtered scenario border selections with local updates to ensure the latest changes are reflected
  const scenarioBorderSelectionsWithLocalUpdates = useMemo(() => {
    if (!scenarioBorderSelectionData) return [];
    return scenarioBorderSelectionData
      .map(
        (scenarioBorderSelection) =>
          scenarioBorderSelectionUpdates.get(scenarioBorderSelection.scenarioTrainId) ??
          scenarioBorderSelection,
      )
      .concat(
        Array.from(scenarioBorderSelectionUpdates.values()).filter(
          (update) =>
            !scenarioBorderSelectionData.some(
              (scenarioBorderSelection) =>
                scenarioBorderSelection.scenarioTrainId === update.scenarioTrainId,
            ),
        ),
      );
  }, [scenarioBorderSelectionData, scenarioBorderSelectionUpdates]);

  const tracks = useMemo(
    () => sbdData?.trackOccupations.map(({ track }) => track) ?? [],
    [sbdData],
  );

  const handleSubmit = useCallback(() => {
    mutateScenarioBorderSelections.mutate(Array.from(scenarioBorderSelectionUpdates.values()), {
      onSuccess: onClose,
    });
  }, [scenarioBorderSelectionUpdates, mutateScenarioBorderSelections, onClose]);

  if (
    appearingScenarioBorderTrainsError ||
    disappearingScenarioBorderTrainsError ||
    scenarioBorderSelectionError ||
    sbdDataError ||
    instanceMetadataError
  ) {
    return <ErrorState />;
  }

  return (
    <SelectScenarioBorders
      appearingScenarioBorderTrains={appearingScenarioBorderTrains ?? []}
      disappearingScenarioBorderTrains={disappearingScenarioBorderTrains ?? []}
      scenarioBorderSelections={scenarioBorderSelectionsWithLocalUpdates}
      tracks={tracks}
      isLoading={
        appearingScenarioBorderTrainsIsLoading ||
        disappearingScenarioBorderTrainsIsLoading ||
        scenarioBorderSelectionIsLoading ||
        sbdDataIsLoading ||
        instanceMetadataIsLoading
      }
      updateScenarioBorderSelection={updateScenarioBorderSelection}
      onSubmit={handleSubmit}
      onClose={onClose}
      scenarioStart={instanceMetadata?.scenarioStart}
      scenarioEnd={instanceMetadata?.scenarioEnd}
    />
  );
}
